import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import {
  Container,
  Header,
  Title,
  Subtitle,
  WorkPartsSection,
  WorkPartsTitle,
  WorkPartsCount,
  WorkPartsList,
  WorkPartCard,
  WorkPartHeader,
  WorkPartName,
  WorkPartStatus,
  WorkPartInfo,
  InfoItem,
  InfoLabel,
  InfoValue,
  ParcelBadges,
  ParcelBadge,
  HoursBadge,
  MainLayout,
  LeftSection,
  CenterSection,
  RightSection,
  StatsCard,
  StatsHeader,
  StatsIcon,
  StatsContent,
  StatsItem,
  StatsLabel,
  StatsValue,
  BigStatsValue,
  BigStatsLabel,
  StatusDot,
} from "./workingPartDetail.styled";
import {
  TimelineSection,
  TimelineTitle,
  ParcelRow,
  ParcelLabel,
  ParcelTimeline,
  DayBox,
} from "./workingPartDetail.timeline.styled";

import { HOUR_TYPES, WORKING_PART_STATUS } from "../../../../constants";
import * as XLSX from "xlsx";
import * as workingPartServices from "../../../../services/WorkingPartServices";
import showDialog from "../../../utils/ShowDialog/ShowDialog";
import { useWorkingPart } from "../hook/useWorkingPart";

import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.min.css";

const CORPORATE_COLORS = [
  "#203b34",
  "#f9ae17",
  "#4a7c59",
  "#ff9800",
  "#607d8b",
  "#e91e63",
  "#9c27b0",
  "#795548",
  "#00bcd4",
  "#8bc34a",
];

const WorkingPartDetailTimelineView = ({
  data,
  uuid,
  onUpdateDays,
  dataConcept,
  dataConcepts,
  onForceRefresh,
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState({});
  const [modifiedDays, setModifiedDays] = useState({});
  const { putConcepts } = useWorkingPart(onForceRefresh);
  const gridRef = useRef(null);

  // Sincronizar modifiedDays cuando cambian los datos
  React.useEffect(() => {
    if (data?.societyJourneis) {
      const initial = {};
      data.societyJourneis.forEach((society) => {
        initial[society.nifSociety] = [...society.days];
      });
      setModifiedDays(initial);
    }
  }, [data?.societyJourneis]);

  const parcelColors = useMemo(() => {
    const parcels = Object.keys(data?.hoursByParcel || {});
    const colors = {};
    parcels.forEach((parcel, index) => {
      colors[parcel] = CORPORATE_COLORS[index % CORPORATE_COLORS.length];
    });
    return colors;
  }, [data]);

  const societyColors = useMemo(() => {
    const societies = data?.societyJourneis || [];
    const colors = {};
    societies.forEach((society, index) => {
      colors[society.nifSociety] =
        CORPORATE_COLORS[index % CORPORATE_COLORS.length];
    });
    return colors;
  }, [data]);

  const timelineData = useMemo(() => {
    if (!data) return { societies: [], days: [] };
    const { year, month, societyJourneis } = data;
    if (!societyJourneis || societyJourneis.length === 0)
      return { societies: [], days: [] };

    const totalDaysInMonth = moment(`${year}-${month}`, "YYYY-M").daysInMonth();

    // Generate all days of the month with their info
    const allDays = [];
    for (let day = 1; day <= totalDaysInMonth; day++) {
      const date = moment(`${year}-${month}-${day}`, "YYYY-M-D");
      const dayOfWeek = date.isoWeekday(); // 1=Monday, 7=Sunday
      allDays.push({
        day,
        dayOfWeek,
        dayName: [
          t("forms.workingPartDetail.days.mon"),
          t("forms.workingPartDetail.days.tue"),
          t("forms.workingPartDetail.days.wed"),
          t("forms.workingPartDetail.days.thu"),
          t("forms.workingPartDetail.days.fri"),
          t("forms.workingPartDetail.days.sat"),
          t("forms.workingPartDetail.days.sun"),
        ][dayOfWeek - 1],
        societies: [],
      });
    }

    // Fill days based on societyJourneis
    societyJourneis.forEach((society) => {
      const {
        nifSociety,
        societyName,
        days: workDays,
        daysCompensation,
      } = society;

      workDays.forEach((dayNumber) => {
        const calDay = allDays.find((d) => d.day === dayNumber);
        if (calDay) {
          const isCompensation =
            daysCompensation && daysCompensation.includes(dayNumber);
          calDay.societies.push({
            nifSociety,
            societyName,
            color: societyColors[nifSociety],
            isCompensation,
          });
        }
      });
    });

    // Create timeline data for each society
    const societyTimelines = societyJourneis.map((society) => ({
      nifSociety: society.nifSociety,
      societyName: society.societyName,
      dayControl: society.dayControl,
      totalDays: society.days.length,
      color: societyColors[society.nifSociety],
    }));

    return {
      societies: societyTimelines,
      days: allDays,
    };
  }, [data, societyColors, t]);

  const extraDaysBySociety = useMemo(() => {
    const extras = data?.societyPeonadasExtras;
    const map = {};

    if (!extras) return map;

    if (Array.isArray(extras)) {
      extras.forEach((entry) => {
        if (typeof entry === "number") return;

        const nifSociety = entry?.nifSociety;
        if (!nifSociety) return;

        const rawDays = Array.isArray(entry.days)
          ? entry.days
          : Array.isArray(entry.peonadasExtras)
            ? entry.peonadasExtras
            : [];

        map[nifSociety] = new Set(rawDays);
      });
      return map;
    }

    if (typeof extras === "object") {
      Object.entries(extras).forEach(([nifSociety, rawDays]) => {
        if (Array.isArray(rawDays)) {
          map[nifSociety] = new Set(rawDays);
        }
      });
    }

    return map;
  }, [data?.societyPeonadasExtras]);

  const toggleEditMode = (nifSociety) => {
    setEditMode((prev) => ({
      ...prev,
      [nifSociety]: !prev[nifSociety],
    }));
  };

  const handleDayToggle = (nifSociety, day, dayOfWeek) => {
    // Bloquear domingos (dayOfWeek === 7)
    if (dayOfWeek === 7) return;

    setModifiedDays((prev) => {
      const currentDays = prev[nifSociety] || [];
      const dayExists = currentDays.includes(day);

      // Verificar si el día está siendo usado por otra sociedad
      if (!dayExists) {
        const isDayUsedByOtherSociety = Object.entries(prev).some(
          ([otherSociety, otherDays]) => {
            return otherSociety !== nifSociety && otherDays.includes(day);
          },
        );

        if (isDayUsedByOtherSociety) {
          alert(
            t("forms.workingPartDetail.messages.dayAlreadyAssigned", { day }),
          );
          return prev;
        }
      }

      if (dayExists) {
        // Quitar el día
        return {
          ...prev,
          [nifSociety]: currentDays.filter((d) => d !== day),
        };
      } else {
        // Añadir el día
        return {
          ...prev,
          [nifSociety]: [...currentDays, day].sort((a, b) => a - b),
        };
      }
    });
  };

  const confirmChanges = async (nifSociety) => {
    const society = data.societyJourneis.find(
      (s) => s.nifSociety === nifSociety,
    );
    const newDays = modifiedDays[nifSociety] || [];

    // Validar que el dayControl sea igual
    if (newDays.length !== society.dayControl) {
      alert(
        t("forms.workingPartDetail.messages.mustMaintainDays", {
          required: society.dayControl,
          current: newDays.length,
        }),
      );
      return;
    }

    // Enviar al backend con la nueva lista de días
    if (onUpdateDays) {
      const result = await onUpdateDays(nifSociety, newDays);
      if (result) {
        toggleEditMode(nifSociety);
      }
    }
  };

  const cancelChanges = (nifSociety) => {
    // Restaurar días originales
    const society = data.societyJourneis.find(
      (s) => s.nifSociety === nifSociety,
    );
    if (society) {
      setModifiedDays((prev) => ({
        ...prev,
        [nifSociety]: [...society.days],
      }));
    }
    toggleEditMode(nifSociety);
  };

  useEffect(() => {
    if (!gridRef.current) return;

    const grid = GridStack.init(
      {
        column: 12,
        cellHeight: 180,
        float: true,
      },
      gridRef.current,
    );

    return () => grid.destroy(false);
  }, []);

  if (!data) {
    return <Container>{t("forms.workingPartDetail.loading")}</Container>;
  }

  const { year, month, workingParts } = data;

  const getHourTypeMeta = (hourType) => {
    const parsedType = Number(hourType);

    const map = {
      1: { icon: "🕒", unit: "h" },
      2: { icon: "⚡", unit: "h" },
      3: { icon: "🏖️", unit: "h" },
      4: { icon: "🧪", unit: "h" },
      5: { icon: "🌙", unit: "h" },
      6: { icon: "🔄", unit: "h" },
      8: { icon: "📍", unit: "km" },
    };

    return map[parsedType] || { icon: "⏱️", unit: "h" };
  };

  const formatHours = (value) => Number(value || 0).toFixed(2);

  const monthName = t(
    `months.${
      [
        "jan",
        "feb",
        "mar",
        "apr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dec",
      ][month - 1]
    }`,
  );

  const exportToExcel = async (data) => {
    let nameEmployee = data.employeeName;
    let nifEmployee = data.employeeData?.nif;
    let contractType = data.employeeData?.indefinedContract
      ? t("forms.workingPartDetail.labels.indefinite")
      : t("forms.workingPartDetail.labels.temporary");
    let companyCode = data.employeeData?.companyCode;
    let idEmployee = data.employeeID;
    let month = data.month;
    let year = data.year;
    let totalHours = data.totalHours;
    let peonadas = data.peonadas;
    let kms = data.totalKms;
    let parts = data.totalParts;
    let parcels;
    let hourType;
    let status;
    let fila;

    const workParts = data.workingParts.map((item) => {
      parcels = item.parcelsSnap?.map((p) => p.name).join(", ") || "";
      hourType =
        t(HOUR_TYPES.find((ht) => ht.code === item.hourType)?.label) ||
        item.hourType;
      status =
        t(
          WORKING_PART_STATUS.find((s) => s.code === parseInt(item.status))
            ?.label,
        ) || item.status;
      fila = {
        A: moment(item.date).format("DD/MM/YYYY"),
        B: item.amount,
        C: parcels,
        D: item.parcelsSnap[0].farm?.name,
        E: hourType,
        F: status,
        G: item.fitos
          ? t("forms.workingPartDetail.yes")
          : t("forms.workingPartDetail.no"),
      };
      return fila;
    });

    const headers = {
      A: "Fecha",
      B: "Cantidad",
      C: "Parcelas",
      D: "Finca",
      E: "Tipo de hora",
      F: "Estado",
      G: "Fitos",
    };

    const summaryRow1 = {
      A: "Nombre Empleado",
      B: "NIF",
      C: "Tipo de Contrato",
      D: "Código Empresa",
      E: "Código Empleado",
      F: "Mes",
      G: "Año",
      H: "Horas Totales",
      I: "Peonadas",
      J: "Kms",
      K: "Nº Partes",
    };
    const summaryRow2 = {
      A: nameEmployee,
      B: nifEmployee,
      C: contractType,
      D: companyCode,
      E: idEmployee,
      F: month,
      G: year,
      H: totalHours,
      I: peonadas,
      J: kms,
      K: parts,
    };
    const summaryRow3 = {
      A: "",
      B: "",
      C: "",
      D: "",
      E: "",
      F: "",
      G: "",
      H: "",
      I: "",
      J: "",
      K: "",
    };

    // workParts.unshift(headers);
    workParts.unshift(summaryRow3);
    workParts.unshift(summaryRow2);
    workParts.unshift(summaryRow1);

    workParts.splice(3, 0, headers);

    const filename = `Resumen_${moment(new Date()).format("DDMMYYYYHHmm")}`;
    const sheetName = "Resumen_Partes_Trabajo";
    var ws = XLSX.utils.json_to_sheet(workParts, {
      header: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"],
      skipHeader: true,
      UTC: true,
      dateNF: "dd-mm-yyyy hh:mm;@",
    });

    // dateNF:'mm/dd/yyyy;@',cellDates:true, raw: true}
    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Partes Trabajo - Resumen",
      Subject: "Exportación de partes de trabajo",
      Author: "Assista",
      CreatedDate: new Date(),
    };
    wb.SheetNames.push(sheetName);
    wb.Sheets[sheetName] = ws;
    XLSX.writeFile(wb, filename + ".xlsx", {
      bookType: "xlsx",
      type: "binary",
    });
  };

  const hoursBySocieties = async (data) => {
    const text = data.hoursBySociety
      .map((item) => {
        let societyText = `<strong>${item.societyName}</strong><br />`;

        Object.entries(item.hoursByType).forEach(([type, hours]) => {
          const typeInfo = HOUR_TYPES.find((ht) => ht.code === parseInt(type));
          const typeName = typeInfo ? t(typeInfo.label) : `Tipo ${type}`;
          societyText += `${typeName}: ${hours}${
            type === "8" ? "km" : "h"
          }<br />`;
        });

        return societyText.trim();
      })
      .join(`<br />`);

    return text;
  };

  const sendA3 = async (data) => {
    const okAction = async () => {
      try {
        let result = await workingPartServices.sendWorkingPartToA3({
          snapshotUuid: data.uuid,
        });
        let title, text, icon;
        if (result) {
          title = t("forms.workingPartDetail.messages.concepts.title");
          text = t("forms.workingPartDetail.messages.concepts.success");
          icon = "success";
        } else {
          title = "Error";
          text = t("forms.workingPartDetail.messages.concepts.error");
          icon = "error";
        }

        showDialog(title, text, icon, false, "OK", false);
      } catch (error) {
        console.error("Error al enviar conceptos a A3:", error);
      }
    };
    showDialog(
      t("forms.workingPartDetail.messages.concepts.title"),
      await hoursBySocieties(data),
      "warning",
      true,
      t("forms.workingPartDetail.messages.concepts.confirmButton"),
      false,
      okAction,
      t("forms.workingPartDetail.messages.concepts.cancelButton"),
    );
  };

  const sendJourneysA3 = async (data) => {
    const okAction = async () => {
      try {
        let result = await workingPartServices.sendJourneysToA3({
          snapshotUuid: data.uuid,
        });

        let title, text, icon;
        if (result) {
          title = t("forms.workingPartDetail.messages.journey.title");
          text = t("forms.workingPartDetail.messages.journey.success");
          icon = "success";
        } else {
          title = "Error";
          text = t("forms.workingPartDetail.messages.journey.error");
          icon = "error";
        }

        showDialog(title, text, icon, false, "OK", false);
      } catch (error) {
        console.error("Error al enviar jornadas a A3:", error);
      }
    };

    showDialog(
      t("forms.workingPartDetail.messages.journey.title"),
      t("forms.workingPartDetail.messages.journey.confirmText"),
      "warning",
      true,
      t("forms.workingPartDetail.messages.journey.confirmButton"),
      false,
      okAction,
      t("forms.workingPartDetail.messages.journey.cancelButton"),
    );
  };

  const handleOnPutConcepts = async (array = null, isMultiple = false) => {
    try {
      await putConcepts(data.uuid, array, isMultiple);
    } catch (error) {}
  };

  return (
    <Container>
      <Header>
        <Title>{t("forms.workingPartDetail.title")}</Title>
        <Subtitle className="justify-content-between align-items-center d-flex">
          <div>
            {`${data.employeeName} - ${monthName} ${year} - ${t(
              "forms.workingPartDetail.labels.contractType",
            )}: ${
              data.employeeData?.indefinedContract
                ? t("forms.workingPartDetail.labels.indefinite")
                : t("forms.workingPartDetail.labels.temporary")
            }`}
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-secondary"
              onClick={() => exportToExcel(data)}
            >
              {t("forms.workingPartDetail.buttons.exportExcel")}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => sendJourneysA3(data)}
            >
              {t("forms.workingPartDetail.buttons.sendJourneys")}
            </button>
            {/* <button className="btn btn-secondary" onClick={() => sendA3(data)}>
              {t("forms.workingPartDetail.buttons.sendConcepts")}
            </button> */}
            <button
              className={`btn btn-secondary ${
                dataConcept.status === "SENT" ? "disabled" : ""
              }`}
              onClick={() => handleOnPutConcepts()}
            >
              {t("forms.workingPartDetail.buttons.sendConcepts")}
            </button>
          </div>
        </Subtitle>
      </Header>

      {timelineData.societies && timelineData.societies.length > 0 && (
        <TimelineSection>
          <TimelineTitle>
            {t("forms.workingPartDetail.distributionBySocieties")}
            {data?.totalHours !== undefined && (
              <span
                style={{
                  marginLeft: "20px",
                  fontSize: "14px",
                  fontWeight: "normal",
                }}
              >
                {t("forms.workingPartDetail.total")}:{" "}
                {data.totalHours.toFixed(2)}h
              </span>
            )}
          </TimelineTitle>
          {timelineData.societies.map((timeline) => {
            const isEditing = editMode[timeline.nifSociety];
            const currentDays = modifiedDays[timeline.nifSociety] || [];
            const dayControl = timeline.dayControl;
            const hasChanges = currentDays.length !== dayControl;

            return (
              <ParcelRow key={timeline.nifSociety}>
                <ParcelLabel color={timeline.color}>
                  {timeline.societyName}&nbsp;&nbsp;
                  <span>
                    {t("forms.workingPartDetail.daysCount", {
                      count: currentDays.length,
                    })}
                    &nbsp;&nbsp;
                    {hasChanges && isEditing && (
                      <span style={{ color: "#f44336", marginLeft: "5px" }}>
                        ⚠
                      </span>
                    )}
                  </span>
                  <div
                    style={{ marginLeft: "auto", display: "flex", gap: "5px" }}
                  >
                    {!isEditing ? (
                      <button
                        onClick={() => toggleEditMode(timeline.nifSociety)}
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px",
                          cursor: "pointer",
                          backgroundColor: timeline.color,
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                        }}
                      >
                        {t("forms.workingPartDetail.buttons.edit")}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => confirmChanges(timeline.nifSociety)}
                          disabled={hasChanges}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                            cursor: hasChanges ? "not-allowed" : "pointer",
                            backgroundColor: hasChanges ? "#ccc" : "#4caf50",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                          }}
                        >
                          {t("forms.workingPartDetail.buttons.confirm")}
                        </button>
                        <button
                          onClick={() => cancelChanges(timeline.nifSociety)}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                            cursor: "pointer",
                            backgroundColor: "#f44336",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                          }}
                        >
                          {t("forms.workingPartDetail.buttons.cancel")}
                        </button>
                      </>
                    )}
                  </div>
                </ParcelLabel>
                <ParcelTimeline>
                  {timelineData.days.map((day) => {
                    const isFilled = currentDays.includes(day.day);
                    const isSunday = day.dayOfWeek === 7;
                    const isExtraDay = Boolean(
                      extraDaysBySociety[timeline.nifSociety]?.has(day.day),
                    );
                    const isExtraFilledDay = isFilled && isExtraDay;

                    // Verificar si es día de compensación para esta sociedad
                    const society = data.societyJourneis.find(
                      (s) => s.nifSociety === timeline.nifSociety,
                    );
                    const isCompensation =
                      society?.daysCompensation?.includes(day.day) || false;

                    // Verificar si el día está ocupado por otra sociedad
                    const isOccupiedByOther = Object.entries(modifiedDays).some(
                      ([otherSociety, otherDays]) => {
                        return (
                          otherSociety !== timeline.nifSociety &&
                          otherDays.includes(day.day)
                        );
                      },
                    );

                    const isBlocked =
                      isSunday ||
                      isOccupiedByOther ||
                      isCompensation ||
                      isExtraFilledDay;
                    const percentage = isFilled ? 100 : 0;
                    const baseTitle = isSunday
                      ? t(
                          "forms.workingPartDetail.tooltips.sundayNotAvailable",
                          { day: day.day, dayName: day.dayName },
                        )
                      : isCompensation
                        ? t(
                            "forms.workingPartDetail.tooltips.compensationDay",
                            {
                              day: day.day,
                              dayName: day.dayName,
                            },
                          )
                        : isExtraFilledDay
                          ? t(
                              "forms.workingPartDetail.tooltips.extraDayBlocked",
                              {
                                day: day.day,
                                dayName: day.dayName,
                              },
                            )
                          : isOccupiedByOther && isEditing
                            ? t(
                                "forms.workingPartDetail.tooltips.occupiedByOther",
                                { day: day.day, dayName: day.dayName },
                              )
                            : isEditing
                              ? isFilled
                                ? t(
                                    "forms.workingPartDetail.tooltips.clickToRemove",
                                    { day: day.day, dayName: day.dayName },
                                  )
                                : t(
                                    "forms.workingPartDetail.tooltips.clickToAdd",
                                    { day: day.day, dayName: day.dayName },
                                  )
                              : isFilled
                                ? t(
                                    "forms.workingPartDetail.tooltips.workday",
                                    {
                                      day: day.day,
                                      dayName: day.dayName,
                                    },
                                  )
                                : t("forms.workingPartDetail.tooltips.day", {
                                    day: day.day,
                                    dayName: day.dayName,
                                  });

                    return (
                      <DayBox
                        key={day.day}
                        color={timeline.color}
                        percentage={percentage}
                        isFilled={isFilled}
                        isRemoved={false}
                        isCompensation={isCompensation}
                        isExtraDay={isExtraFilledDay}
                        canRestore={isEditing && !isBlocked}
                        title={baseTitle}
                        onClick={() => {
                          if (isEditing && !isBlocked) {
                            handleDayToggle(
                              timeline.nifSociety,
                              day.day,
                              day.dayOfWeek,
                            );
                          }
                        }}
                        style={{
                          cursor:
                            isEditing && !isBlocked ? "pointer" : "default",
                          opacity: isBlocked ? 0.3 : 1,
                        }}
                      >
                        <div className="day-name">
                          {day.dayOfWeek === 3
                            ? "X"
                            : day.dayName.substring(0, 1)}
                        </div>
                        <div className="day-number">{day.day}</div>
                      </DayBox>
                    );
                  })}
                </ParcelTimeline>
              </ParcelRow>
            );
          })}
        </TimelineSection>
      )}

      <MainLayout>
        {/* <div ref={gridRef} className="grid-stack">
          <div className="grid-stack-item" gs-w="3" gs-h="4" gs-x="0" gs-y="0">
            <div className="grid-stack-item-content"> */}
        <LeftSection>
          {/* Hours by Type */}
          {data?.hoursByType && Object.keys(data.hoursByType).length > 0 && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>📝</StatsIcon>
                {t("forms.workingPartDetail.stats.hoursByType") ||
                  "Hours by Type"}
              </StatsHeader>
              <StatsContent>
                {Object.entries(data.hoursByType).map(([type, hours]) => {
                  const typeInfo = HOUR_TYPES.find(
                    (ht) => ht.code === parseInt(type),
                  );
                  const typeMeta = getHourTypeMeta(type);
                  return (
                    <StatsItem key={type}>
                      <StatsLabel>
                        {typeMeta.icon}{" "}
                        {typeInfo ? t(typeInfo.label) : `Type ${type}`}
                      </StatsLabel>
                      <StatsValue>
                        {hours.toFixed(2)}
                        {typeMeta.unit}
                      </StatsValue>
                    </StatsItem>
                  );
                })}
              </StatsContent>
            </StatsCard>
          )}
          {/* Hours by Parcel */}
          {data?.hoursByParcel &&
            Object.keys(data.hoursByParcel).length > 0 && (
              <StatsCard>
                <StatsHeader>
                  <StatsIcon>🕐</StatsIcon>
                  {t("forms.workingPartDetail.stats.hoursByParcel") ||
                    "Hours by Parcel"}
                </StatsHeader>
                <StatsContent style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {Object.entries(data.hoursByParcel).map(
                    ([parcelId, hours]) => {
                      // Buscar el nombre de la parcela en workingParts
                      let parcelName = `Parcel ${parcelId}`;
                      let farmName;
                      if (data.workingParts) {
                        for (const part of data.workingParts) {
                          if (part.parcelsSnap) {
                            const parcel = part.parcelsSnap.find(
                              (p) => p.id === parseInt(parcelId),
                            );
                            if (parcel) {
                              parcelName = parcel.name;
                              farmName = parcel.farm?.name || "";
                              break;
                            }
                          }
                        }
                      }
                      return (
                        <StatsItem key={parcelId}>
                          <StatsLabel>
                            {parcelName} ({farmName})
                          </StatsLabel>
                          <StatsValue>{hours.toFixed(1)}h</StatsValue>
                        </StatsItem>
                      );
                    },
                  )}
                  {/* {Object.entries(data.hoursByParcel).map(([parcel, hours]) => (
                    <StatsItem key={parcel}>
                      <StatsLabel>Parcel {parcel}</StatsLabel>
                      <StatsValue>
                        {typeof hours === "number" ? hours.toFixed(0) : hours}h
                      </StatsValue>
                    </StatsItem>
                  ))} */}
                </StatsContent>
              </StatsCard>
            )}
          {/* Absence Hours by Parcel */}
          {data?.absenceHoursByParcel &&
            Object.keys(data.absenceHoursByParcel).length > 0 && (
              <StatsCard>
                <StatsHeader>
                  <StatsIcon>⏰</StatsIcon>
                  {t("forms.workingPartDetail.stats.absenceHoursByParcel") ||
                    "Absence Hours by Parcel"}
                </StatsHeader>
                <StatsContent>
                  {Object.entries(data.absenceHoursByParcel).map(
                    ([parcelId, hours]) => {
                      // Buscar el nombre de la parcela en workingParts
                      let parcelName = `Parcel ${parcelId}`;
                      let farmName;
                      if (data.workingParts) {
                        for (const part of data.workingParts) {
                          if (part.parcelsSnap) {
                            const parcel = part.parcelsSnap.find(
                              (p) => p.id === parseInt(parcelId),
                            );
                            if (parcel) {
                              parcelName = parcel.name;
                              farmName = parcel.farm?.name || "";
                              break;
                            }
                          }
                        }
                      }
                      return (
                        <StatsItem key={parcelId}>
                          <StatsLabel>
                            {parcelName} ({farmName})
                          </StatsLabel>
                          <StatsValue>{hours.toFixed(1)}h</StatsValue>
                        </StatsItem>
                      );
                    },
                  )}
                </StatsContent>
              </StatsCard>
            )}
          {workingParts && workingParts.length > 0 && (
            <WorkPartsSection>
              <WorkPartsTitle>
                {t("forms.workingPartDetail.workParts")}
                <WorkPartsCount>
                  {t("forms.workingPartDetail.entries", {
                    count: workingParts.length,
                  })}
                </WorkPartsCount>
              </WorkPartsTitle>
              <WorkPartsList>
                {workingParts.map((part) => (
                  <WorkPartCard key={part.uuid}>
                    <WorkPartHeader>
                      <WorkPartName>
                        {part.taskName || "Parte de Trabajo"}
                      </WorkPartName>
                      {part.parcelsSnap && part.parcelsSnap.length > 0 && (
                        <ParcelBadges
                          style={{ flex: 1, justifyContent: "center" }}
                        >
                          {part.parcelsSnap.map((parcel) => (
                            <ParcelBadge
                              key={parcel.name}
                              color={parcelColors[parcel.id] || "#203b34"}
                            >
                              {parcel.name}
                            </ParcelBadge>
                          ))}
                        </ParcelBadges>
                      )}
                      <WorkPartStatus status={part.status}>
                        {t(
                          WORKING_PART_STATUS.find(
                            (s) => s.code === part.status,
                          )?.label,
                        )}
                      </WorkPartStatus>
                    </WorkPartHeader>
                    <WorkPartInfo>
                      <InfoItem>
                        <InfoLabel>
                          {t("forms.workingPartDetail.labels.date") || "Fecha"}:
                        </InfoLabel>
                        <InfoValue>
                          {moment(part.date).format("DD/MM/YYYY")}
                        </InfoValue>
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>
                          {part.hourType === 8
                            ? t("forms.workingPartDetail.labels.kilometers")
                            : t("forms.workingPartDetail.labels.hours") ||
                              "Horas"}
                          :
                        </InfoLabel>
                        <InfoValue>
                          <HoursBadge>
                            {getHourTypeMeta(part.hourType).icon}{" "}
                            {part.amount.toFixed(2)}{" "}
                            {getHourTypeMeta(part.hourType).unit}
                          </HoursBadge>
                        </InfoValue>
                      </InfoItem>

                      {part.hourType !== undefined && (
                        <InfoItem>
                          <InfoLabel>
                            {t("forms.workingPartDetail.labels.hourType") ||
                              "Tipo de Hora"}
                            :
                          </InfoLabel>
                          <InfoValue>
                            {t(
                              HOUR_TYPES.find((ht) => ht.code === part.hourType)
                                ?.label,
                            ) || `Tipo ${part.hourType}`}
                          </InfoValue>
                        </InfoItem>
                      )}

                      {part.worker && (
                        <InfoItem>
                          <InfoLabel>Trabajador:</InfoLabel>
                          <InfoValue>{part.worker}</InfoValue>
                        </InfoItem>
                      )}

                      {part.fitos && part.fitos.length > 0 && (
                        <InfoItem>
                          <InfoLabel>
                            {t("forms.workingPartDetail.labels.fitos") ||
                              "Fitos"}
                            :
                          </InfoLabel>
                          <InfoValue>{part.fitos.join(", ")}</InfoValue>
                        </InfoItem>
                      )}
                    </WorkPartInfo>
                  </WorkPartCard>
                ))}
              </WorkPartsList>
            </WorkPartsSection>
          )}
        </LeftSection>
        <CenterSection>
          {/* Total Normal */}
          {data.hoursByType && data.hoursByType["1"] && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>🕒</StatsIcon>
                {t("forms.workingPartDetail.stats.totalNormal") ||
                  "Total Normal"}
              </StatsHeader>
              <BigStatsValue>
                {formatHours(data.hoursByType ? data.hoursByType["1"] : 0)}
              </BigStatsValue>
              <BigStatsLabel>H</BigStatsLabel>
            </StatsCard>
          )}
          {/* Total extra */}
          {data.hoursByType && data.hoursByType["2"] && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>⚡</StatsIcon>
                {t("forms.workingPartDetail.stats.totalExtra") || "Total Extra"}
              </StatsHeader>
              <BigStatsValue>
                {formatHours(data.hoursByType ? data.hoursByType["2"] : 0)}
              </BigStatsValue>
              <BigStatsLabel>H</BigStatsLabel>
            </StatsCard>
          )}

          {/* Total nocturna */}
          {data.hoursByType && data.hoursByType["5"] && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>🌙</StatsIcon>
                {t("forms.workingPartDetail.stats.totalNocturna") ||
                  "Total Nocturna"}
              </StatsHeader>
              <BigStatsValue>
                {formatHours(data.hoursByType ? data.hoursByType["5"] : 0)}
              </BigStatsValue>
              <BigStatsLabel>H</BigStatsLabel>
            </StatsCard>
          )}
          {/* Total fito */}
          {data.hoursByType && data.hoursByType["4"] && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>🧪</StatsIcon>
                {t("forms.workingPartDetail.stats.totalFito") || "Total Fito"}
              </StatsHeader>
              <BigStatsValue>
                {formatHours(data.hoursByType ? data.hoursByType["4"] : 0)}
              </BigStatsValue>
              <BigStatsLabel>H</BigStatsLabel>
            </StatsCard>
          )}

          {/* Total compensacion */}
          {data.hoursByType && data.hoursByType["6"] && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>🔄</StatsIcon>
                {t("forms.workingPartDetail.stats.totalCompensacion") ||
                  "Total Compensacion"}
              </StatsHeader>
              <BigStatsValue>
                {formatHours(data.hoursByType ? data.hoursByType["6"] : 0)}
              </BigStatsValue>
              <BigStatsLabel>H</BigStatsLabel>
            </StatsCard>
          )}

          {/* Kms by Society */}
          {data?.kmsSociety && data.kmsSociety.length > 0 && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>📍</StatsIcon>
                {t("forms.workingPartDetail.stats.kmsBySociety") ||
                  "Kms by Society"}
              </StatsHeader>
              <StatsContent>
                {data.kmsSociety.map((item) => (
                  <StatsItem key={item.nifSociety}>
                    <StatsLabel>{item.societyName}</StatsLabel>
                    <StatsValue>{item.hours} km</StatsValue>
                  </StatsItem>
                ))}
              </StatsContent>
            </StatsCard>
          )}

          {/* Total Kilometers */}
          {data?.totalKms !== 0 && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>🚗</StatsIcon>
                {t("forms.workingPartDetail.stats.totalKilometers") ||
                  "Total Kilometers"}
              </StatsHeader>
              <BigStatsValue>{data.totalKms}</BigStatsValue>
              <BigStatsLabel>km</BigStatsLabel>
            </StatsCard>
          )}

          {/* Total Parts */}
          {data?.totalParts !== undefined && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>📋</StatsIcon>
                {t("forms.workingPartDetail.stats.totalParts") || "Total Parts"}
              </StatsHeader>
              <BigStatsValue>{data.totalParts}</BigStatsValue>
              <BigStatsLabel>partes</BigStatsLabel>
            </StatsCard>
          )}

          {/* Peonadas */}
          {data?.peonadas !== undefined && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>⏱️</StatsIcon>
                {t("forms.workingPartDetail.stats.peonadas") || "Peonadas"}
              </StatsHeader>
              <BigStatsValue>{Math.trunc(data.peonadas)}</BigStatsValue>
              <BigStatsLabel>jornadas</BigStatsLabel>
            </StatsCard>
          )}

          {/* Parts by Status */}
          {data?.partsByStatus &&
            Object.keys(data.partsByStatus).length > 0 && (
              <StatsCard>
                <StatsHeader>
                  <StatsIcon>📊</StatsIcon>
                  {t("forms.workingPartDetail.stats.partsByStatus") ||
                    "Parts by Status"}
                </StatsHeader>
                <StatsContent>
                  {Object.entries(data.partsByStatus).map(([status, count]) => {
                    const statusInfo = WORKING_PART_STATUS.find(
                      (s) => s.code === parseInt(status),
                    );
                    const statusColor =
                      status === "2"
                        ? "#4caf50"
                        : status === "3"
                          ? "#ff9800"
                          : "#f44336";
                    return (
                      <StatsItem key={status}>
                        <StatsLabel>
                          <StatusDot color={statusColor} />
                          {statusInfo
                            ? t(statusInfo.label)
                            : `Status ${status}`}
                        </StatsLabel>
                        <StatsValue>{count}</StatsValue>
                      </StatsItem>
                    );
                  })}
                </StatsContent>
              </StatsCard>
            )}
          {/* Total ausencia */}
          {data.hoursByType && data.hoursByType["3"] && (
            <StatsCard>
              <StatsHeader>
                <StatsIcon>🏖️</StatsIcon>
                {t("forms.workingPartDetail.stats.totalAbsence") ||
                  "Total Absence"}
              </StatsHeader>
              <BigStatsValue>
                {formatHours(data.hoursByType ? data.hoursByType["3"] : 0)}
              </BigStatsValue>
              <BigStatsLabel>H</BigStatsLabel>
            </StatsCard>
          )}
        </CenterSection>

        {/* <RightSection>
          {workingParts && workingParts.length > 0 && (
            <WorkPartsSection>
              <WorkPartsTitle>
                {t("forms.workingPartDetail.workParts")}
                <WorkPartsCount>
                  {t("forms.workingPartDetail.entries", {
                    count: workingParts.length,
                  })}
                </WorkPartsCount>
              </WorkPartsTitle>
              <WorkPartsList>
                {workingParts.map((part) => (
                  <WorkPartCard key={part.uuid}>
                    <WorkPartHeader>
                      <WorkPartName>
                        {part.taskName || t("forms.workingPartDetail.labels.workPart")}
                      </WorkPartName>
                      {part.parcelsSnap && part.parcelsSnap.length > 0 && (
                        <ParcelBadges
                          style={{ flex: 1, justifyContent: "center" }}
                        >
                          {part.parcelsSnap.map((parcel) => (
                            <ParcelBadge
                              key={parcel.name}
                              color={parcelColors[parcel.id] || "#203b34"}
                            >
                              {parcel.name}
                            </ParcelBadge>
                          ))}
                        </ParcelBadges>
                      )}
                      <WorkPartStatus status={part.status}>
                        {t(
                          WORKING_PART_STATUS.find(
                            (s) => s.code === part.status,
                          )?.label,
                        )}
                      </WorkPartStatus>
                    </WorkPartHeader>
                    <WorkPartInfo>
                      <InfoItem>
                        <InfoLabel>
                          {t("forms.workingPartDetail.labels.date") || "Fecha"}:
                        </InfoLabel>
                        <InfoValue>
                          {moment(part.date).format("DD/MM/YYYY")}
                        </InfoValue>
                      </InfoItem>

                      <InfoItem>
                        <InfoLabel>
                          {part.hourType === 8
                            ? t("forms.workingPartDetail.labels.kilometers")
                            : t("forms.workingPartDetail.labels.hours") ||
                              "Horas"}
                          :
                        </InfoLabel>
                        <InfoValue>
                          <HoursBadge>
                            {getHourTypeMeta(part.hourType).icon} {part.amount.toFixed(2)}{" "}
                            {getHourTypeMeta(part.hourType).unit}
                          </HoursBadge>
                        </InfoValue>
                      </InfoItem>

                      {part.hourType !== undefined && (
                        <InfoItem>
                          <InfoLabel>
                            {t("forms.workingPartDetail.labels.hourType") ||
                              "Tipo de Hora"}
                            :
                          </InfoLabel>
                          <InfoValue>
                            {t(
                              HOUR_TYPES.find((ht) => ht.code === part.hourType)
                                ?.label,
                            ) || `Tipo ${part.hourType}`}
                          </InfoValue>
                        </InfoItem>
                      )}

                      {part.worker && (
                        <InfoItem>
                          <InfoLabel>
                            {t("forms.workingPartDetail.labels.worker")}:
                          </InfoLabel>
                          <InfoValue>{part.worker}</InfoValue>
                        </InfoItem>
                      )}

                      {part.fitos && part.fitos.length > 0 && (
                        <InfoItem>
                          <InfoLabel>
                            {t("forms.workingPartDetail.labels.fitos") ||
                              "Fitos"}
                            :
                          </InfoLabel>
                          <InfoValue>{part.fitos.join(", ")}</InfoValue>
                        </InfoItem>
                      )}
                    </WorkPartInfo>
                  </WorkPartCard>
                ))}
              </WorkPartsList>
            </WorkPartsSection>
          )}
        </RightSection> */}

        <RightSection>
          <WorkPartsSection>
            <WorkPartsTitle>
              {t("forms.workingPartDetail.labels.sentConcepts")}
            </WorkPartsTitle>
            {dataConcepts.length !== 0 ? (
              <div className="row justify-content-center  ">
                <div className="col-12 col-md-11">
                  {dataConcepts &&
                    dataConcepts.map((concept, index) => (
                      <div className="border-bottom border-1 ">
                        <React.Fragment key={index}>
                          {concept.items.map((item) => {
                            const itemSent = concept.itemsSent?.find(
                              (sent) => sent.nifSociety === item.nifSociety,
                            );

                            return (
                              <div key={item.nifSociety} className="mb-4 ">
                                <div className="row mt-3">
                                  <div className="col-12 justify-content-center">
                                    <h5 className="mb-3 text-center">
                                      {item.societyName}
                                    </h5>
                                  </div>
                                </div>
                                <div className="row mt-2">
                                  <div className="col-12 col-md-11 d-flex align-items-center justify-content-center">
                                    <div className="row col-md-11">
                                      <div className="col-6 text-center">
                                        <i className="fa-solid fa-check"></i>
                                      </div>
                                      <div className="col-6 text-center">
                                        <i className="fa-solid fa-share-from-square"></i>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {Object.entries(item.hoursByType).map(
                                  ([type, hours]) => {
                                    const typeInfo = HOUR_TYPES.find(
                                      (ht) => ht.code === parseInt(type),
                                    );
                                    const typeName = typeInfo
                                      ? t(typeInfo.label)
                                      : t(
                                          "forms.workingPartDetail.hourTypes.type",
                                          {
                                            type,
                                          },
                                        );
                                    const hoursSent =
                                      itemSent?.hoursByType?.[type] || 0;
                                    const fieldName = `${item.nifSociety}_${type}`;

                                    return (
                                      <div className="row mt-2" key={fieldName}>
                                        <div className="col-12 col-md-11 d-flex align-items-center justify-content-center">
                                          <div className="row col-md-11">
                                            <div className="col-6">
                                              {`${typeName}: ${hours.toFixed(2)}h `}
                                            </div>
                                            <div className="col-6 text-center">
                                              {`${hoursSent.toFixed(2)}h`}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mt-2">
                  <p>{t("forms.workingPartDetail.messages.noSentConcepts")}</p>
                </div>
              </>
            )}
          </WorkPartsSection>
        </RightSection>
      </MainLayout>
    </Container>
  );
};

WorkingPartDetailTimelineView.propTypes = {
  data: PropTypes.shape({
    year: PropTypes.number,
    month: PropTypes.number,
    employeeName: PropTypes.string,
    hoursByParcel: PropTypes.object,
    workingParts: PropTypes.array,
    totalHours: PropTypes.number,
    totalParts: PropTypes.number,
    peonadas: PropTypes.number,
    restHours: PropTypes.number,
    societyJourneis: PropTypes.arrayOf(
      PropTypes.shape({
        nifSociety: PropTypes.string,
        societyName: PropTypes.string,
        days: PropTypes.arrayOf(PropTypes.number),
        dayControl: PropTypes.number,
      }),
    ),
    societyPeonadasExtras: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
    ]),
  }),
  uuid: PropTypes.string,
  onUpdateDays: PropTypes.func,
};

export default WorkingPartDetailTimelineView;
